import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, PhoneOff, Users, Minimize2, Maximize2 } from "lucide-react";
import { useTalk, TkAvatar } from "./TalkLayout";

function PeerAudio({ stream }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream && ref.current.srcObject !== stream) { ref.current.srcObject = stream; ref.current.play().catch(() => {}); } }, [stream]);
  return <audio ref={ref} autoPlay playsInline />;
}

function useClock(start) {
  const [, t] = useState(0);
  useEffect(() => { const i = setInterval(() => t((x) => x + 1), 1000); return () => clearInterval(i); }, []);
  const s = Math.floor((Date.now() - start) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function GroupCallOverlay() {
  const { engine, group, me } = useTalk();
  const [mini, setMini] = useState(false);
  const clock = useClock(group?.startedAt || Date.now());
  if (!group || !engine) return null;
  const peers = Object.entries(group.peers);
  const audio = peers.map(([id, p]) => <PeerAudio key={id} stream={p.stream} />);
  if (mini) return (
    <>{audio}
      <button onClick={() => setMini(false)} data-testid="talk-gcall-mini" className="fixed bottom-24 right-4 z-[70] flex items-center gap-2 bg-[#39FF14] text-black font-black rounded-full px-4 py-2 shadow-2xl">
        <Users className="w-4 h-4" />{group.name} · {peers.length + 1} <Maximize2 className="w-4 h-4" />
      </button>
    </>
  );
  return (
    <div className="fixed inset-0 z-[70] bg-[#050509] flex flex-col" data-testid="talk-gcall-overlay" data-state={group.state}>
      {audio}
      <div className="flex items-center justify-between p-4">
        <span className="mono text-[10px] uppercase tracking-widest text-white/60">Group voice call · {clock}</span>
        <button onClick={() => setMini(true)} data-testid="talk-gcall-minimize" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><Minimize2 className="w-4 h-4" /></button>
      </div>
      <div className="px-6 pt-4">
        <div className="font-heading font-black text-3xl" data-testid="talk-gcall-name">{group.name}</div>
        <div className="text-white/60 text-sm mt-1" data-testid="talk-gcall-count">{group.state === "joining" ? "Joining…" : peers.length ? `${peers.length + 1} on the call` : "Waiting for others to join…"}</div>
      </div>
      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 content-start">
        <div className="flex flex-col items-center gap-2" data-testid="talk-gcall-me">
          <div className="relative"><TkAvatar p={me.profile} size={80} />{group.muted && <MicOff className="absolute -bottom-1 -right-1 w-6 h-6 p-1 rounded-full bg-[#FF003C]" />}</div>
          <span className="text-sm font-bold">You</span>
        </div>
        {peers.map(([id, p]) => (
          <div key={id} className="flex flex-col items-center gap-2" data-testid={`talk-gcall-peer-${p.profile.username}`}>
            <div className={p.state === "connected" ? "" : "opacity-50 animate-pulse"}><TkAvatar p={p.profile} size={80} /></div>
            <span className="text-sm font-bold truncate max-w-[110px]">{p.profile.display_name}</span>
            <span className="text-[10px] mono text-white/40">{p.state === "connected" ? "connected" : p.state === "failed" ? "can't connect" : "connecting…"}</span>
          </div>
        ))}
      </div>
      <div className="pb-12 pt-6 flex items-center justify-center gap-5">
        <button onClick={() => engine.gMute()} data-testid="talk-gcall-mute" aria-label="Mute" className={`w-14 h-14 rounded-full flex items-center justify-center ${group.muted ? "bg-white text-black" : "bg-white/15"}`}>{group.muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}</button>
        <button onClick={() => engine.leaveGroup()} data-testid="talk-gcall-leave" aria-label="Leave call" className="w-14 h-14 rounded-full bg-[#FF003C] flex items-center justify-center"><PhoneOff className="w-6 h-6" /></button>
      </div>
    </div>
  );
}
